<template>
    <div class="invoice" id="printMe">
  
      <!-- Loading -->
      <div v-if="loading" class="invoice-loading">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
      </div>
  
      <template v-else>
  
        <!-- Invoice Header -->
        <div class="invoice-header">
          <div class="invoice-title-block">
            <h1 class="invoice-title">Invoice</h1>
            <span class="invoice-batch">{{ sales?.batch_code }}</span>
          </div>
          <div v-if="company?.logo" class="invoice-logo">
            <img :src="host + company.logo" alt="Company Logo" />
          </div>
        </div>
  
        <div class="invoice-divider"></div>
  
        <!-- Parties Row -->
        <div class="invoice-parties">
          <!-- Bill To -->
          <div class="party-block">
            <p class="party-label">Bill To</p>
            <p class="party-name">{{ sales?.customer?.customer_name || 'Walk-in Customer' }}</p>
            <p class="party-detail" v-if="sales?.customer?.address">{{ sales.customer.address }}</p>
            <p class="party-detail" v-if="sales?.customer?.email">{{ sales.customer.email }}</p>
          </div>
  
          <!-- From -->
          <div class="party-block party-block--right">
            <p class="party-label">From</p>
            <p class="party-name">{{ company?.name }}</p>
            <p class="party-detail" v-if="sales?.store?.address">{{ sales.store.address }}</p>
            <p class="party-detail" v-if="sales?.store?.phone_no || company?.phone_number">
              {{ [sales?.store?.phone_no, company?.phone_number].filter(Boolean).join(', ') }}
            </p>
            <p class="party-detail" v-if="company?.email">{{ company.email }}</p>
          </div>
        </div>
  
        <div class="invoice-divider invoice-divider--dashed"></div>
  
        <!-- Sale Meta -->
        <div class="sale-meta-grid">
          <div class="sale-meta-item">
            <span class="sale-meta-label">Date</span>
            <span class="sale-meta-value">{{ dateTime(sales?.date) }}</span>
          </div>
          <div class="sale-meta-item">
            <span class="sale-meta-label">Sale No.</span>
            <span class="sale-meta-value mono">{{ sales?.batch_code }}</span>
          </div>
          <div class="sale-meta-item">
            <span class="sale-meta-label">Store</span>
            <span class="sale-meta-value">{{ sales?.store?.store_name }}</span>
          </div>
          <div class="sale-meta-item">
            <span class="sale-meta-label">Sold By</span>
            <span class="sale-meta-value">{{ sales?.user?.fullname }}</span>
          </div>
        </div>
  
        <div class="invoice-divider invoice-divider--dashed"></div>
  
        <!-- Items Table -->
        <div class="table-scroll">
          <table class="invoice-table">
            <thead>
              <tr>
                <th style="width:28%">Product</th>
                <th>Variant</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Price ({{ company?.currency?.symbol }})</th>
                <th class="text-right">Discount ({{ company?.currency?.symbol }})</th>
                <th class="text-right">Tax{{ taxLabel }} ({{ company?.currency?.symbol }})</th>
                <th class="text-right">Amount ({{ company?.currency?.symbol }})</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in sales_array" :key="p.id">
                <td class="fw-bold">{{ p?.product_name }}</td>
                <td class="text-muted">{{ p?.variant_name || '—' }}</td>
                <td class="text-right">{{ p?.quantity }}</td>
                <td class="text-right">{{ $filters.formatMoney(p?.selling_price) }}</td>
                <td class="text-right text-muted">{{ p?.discount ? $filters.formatMoney(p.discount) : 'N/A' }}</td>
                <td class="text-right text-muted">{{ p?.tax ? $filters.formatMoney(p.tax) : 'N/A' }}</td>
                <td class="text-right fw-bold">{{ $filters.formatMoney(p?.total) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
  
        <!-- Totals -->
        <div class="totals-block">
          <div class="totals-row">
            <span class="totals-label">Subtotal</span>
            <span class="totals-value">{{ $filters.formatMoney(subTotal, company?.currency?.symbol) }}</span>
          </div>
          <div class="totals-row">
            <span class="totals-label">Discount</span>
            <span class="totals-value totals-value--discount">– {{ $filters.formatMoney(discountTotal, company?.currency?.symbol) }}</span>
          </div>
          <div class="totals-row">
            <span class="totals-label">Tax{{ taxLabel }}</span>
            <span class="totals-value">{{ $filters.formatMoney(taxTotal, company?.currency?.symbol) }}</span>
          </div>
          <div class="totals-divider"></div>
          <div class="totals-row totals-row--grand">
            <span class="totals-label">Total</span>
            <span class="totals-value totals-value--grand">{{ $filters.formatMoney(grandTotal, company?.currency?.symbol) }}</span>
          </div>
        </div>
  
        <div class="invoice-divider"></div>
  
        <!-- Payment Info -->
        <div class="payment-block">
          <p class="payment-title">Payment Information</p>
          <div class="payment-grid">
            <div class="payment-row">
              <span class="payment-label">Payment Type</span>
              <span class="payment-badge" :class="paymentClass(sales?.payment_type)">
                {{ paymentLabel(sales?.payment_type) }}
              </span>
            </div>
            <div class="payment-row">
              <span class="payment-label">Amount Paid</span>
              <span class="payment-value">{{ $filters.formatMoney(sales?.amount_paid, company?.currency?.symbol) }}</span>
            </div>
            <div class="payment-row" v-if="sales?.payment_type === 'cash' || sales?.payment_type === 'card'">
              <span class="payment-label">Change</span>
              <span class="payment-value">{{ $filters.formatMoney(sales?.change, company?.currency?.symbol) }}</span>
            </div>
            <div class="payment-row">
              <span class="payment-label">Balance</span>
              <span class="payment-value" :class="parseFloat(sales?.balance) > 0 ? 'text-neg' : ''">
                {{ $filters.formatMoney(sales?.balance ?? 0, company?.currency?.symbol) }}
              </span>
            </div>
          </div>
        </div>
  
      </template>
    </div>
  </template>
  
  <script>
  import { useToast } from 'vue-toastification';
  import { reusableMixin } from '../../../../lib/reusableMixin';
  import { useStore } from 'vuex';
  import host from '../../../../main';
  
  export default {
    props: {
      sales_items: Array,
      sales_array: { type: Array, default: () => [] },
      sales:       Object,
      loading:     Boolean,
    },
    mixins: [reusableMixin],
  
    data() {
      return {
        toast:   useToast(),
        store:   useStore(),
        company: null,
      };
    },
  
    computed: {
      host()        { return host.hostName; },
      loggedInUser(){ return this.store.state.getLoggedInUser; },
  
      grandTotal() {
        return this.sales_array.reduce((a, b) =>
          (parseFloat(a) + parseFloat(b.total) + parseFloat(b.tax) - parseFloat(b.discount)).toFixed(2), 0);
      },
      subTotal() {
        return this.sales_array.reduce((a, b) => a + parseFloat(b.total), 0);
      },
      discountTotal() {
        return this.sales_array.reduce((a, b) => (parseFloat(a) + parseFloat(b.discount)).toFixed(2), 0);
      },
      taxTotal() {
        return this.sales_array.reduce((a, b) => a + parseFloat(b.tax), 0);
      },
      uniqueTaxNames() {
        const names = new Set();
        this.sales_array.forEach(item => (item.tax_names ?? []).forEach(n => names.add(n)));
        return [...names];
      },
      taxLabel() {
        return this.uniqueTaxNames.length ? ` (${this.uniqueTaxNames.join(', ')})` : '';
      },
    },
  
    created() { this.getCompany(); },
  
    methods: {
      paymentLabel(type) {
        const map = { cash:'Cash', card:'Card', transfer:'Transfer', part:'Part Payment', split:'Split Payment', credit:'Credit' };
        return map[type] ?? (type ? type.charAt(0).toUpperCase() + type.slice(1) : '—');
      },
      paymentClass(type) {
        const map = { cash:'payment--cash', card:'payment--card', transfer:'payment--transfer', part:'payment--part', split:'payment--split', credit:'payment--credit' };
        return map[type] ?? '';
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Invoice Shell ── */
  .invoice { background:#fff; padding:40px 48px; max-width:860px; margin:0 auto; font-size:13.5px; color:#1a2b45; }
  
  /* ── Loading ── */
  .invoice-loading { display:flex; align-items:center; justify-content:center; padding:80px 0; }
  
  /* ── Header ── */
  .invoice-header      { display:flex; align-items:flex-start; justify-content:space-between; gap:20px; margin-bottom:24px; }
  .invoice-title-block { display:flex; flex-direction:column; gap:8px; }
  .invoice-title       { font-size:40px; font-weight:800; color:#1a2b45; margin:0; line-height:1; }
  .invoice-batch       { font-family:monospace; font-size:14px; font-weight:600; color:#004AAD; background:#edf4ff; border:1px solid #c5d6f5; border-radius:20px; padding:3px 14px; display:inline-block; }
  .invoice-logo        { flex-shrink:0; }
  .invoice-logo img    { max-width:120px; max-height:60px; object-fit:contain; }
  
  /* ── Dividers ── */
  .invoice-divider         { height:1px; background:#e4eaf2; margin:20px 0; }
  .invoice-divider--dashed { border:none; border-top:2px dashed #e4eaf2; background:transparent; }
  
  /* ── Parties ── */
  .invoice-parties    { display:flex; justify-content:space-between; gap:20px; flex-wrap:wrap; }
  .party-block        { display:flex; flex-direction:column; gap:3px; }
  .party-block--right { text-align:right; }
  .party-label        { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; margin:0 0 4px; }
  .party-name         { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
  .party-detail       { font-size:13px; color:#4a5e78; margin:0; }
  
  /* ── Sale Meta Grid ── */
  .sale-meta-grid  { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
  .sale-meta-item  { display:flex; flex-direction:column; gap:4px; }
  .sale-meta-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
  .sale-meta-value { font-size:13.5px; font-weight:600; color:#1a2b45; }
  .mono            { font-family:monospace; }
  
  /* ── Items Table ── */
  .table-scroll     { overflow-x:auto; }
  .invoice-table    { width:100%; border-collapse:collapse; }
  .invoice-table thead tr { border-bottom:2px solid #e4eaf2; background:#f8fafc; }
  .invoice-table thead th { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:10px 12px; text-align:left; white-space:nowrap; }
  .invoice-table tbody tr { border-bottom:1px solid #f0f4f8; }
  .invoice-table tbody tr:last-child { border-bottom:none; }
  .invoice-table tbody td { padding:10px 12px; vertical-align:middle; color:#1a2b45; }
  .text-right  { text-align:right; }
  .text-muted  { color:#4a5e78; }
  .fw-bold     { font-weight:700; }
  
  /* ── Totals ── */
  .totals-block  { display:flex; flex-direction:column; align-items:flex-end; gap:0; margin-top:20px; padding-right:0; }
  .totals-row    { display:flex; justify-content:space-between; width:280px; padding:6px 0; }
  .totals-label  { font-size:13px; font-weight:600; color:#7a90b0; text-transform:uppercase; }
  .totals-value  { font-size:13.5px; font-weight:600; color:#1a2b45; }
  .totals-value--discount { color:#e03535; }
  .totals-divider { width:280px; height:1px; background:#e4eaf2; margin:6px 0; }
  .totals-row--grand .totals-label { color:#1a2b45; font-size:14px; }
  .totals-value--grand { font-size:18px; font-weight:800; color:#004AAD; }
  
  /* ── Payment ── */
  .payment-block { padding:0; }
  .payment-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 14px; }
  .payment-grid  { display:flex; flex-direction:column; gap:8px; max-width:360px; }
  .payment-row   { display:flex; justify-content:space-between; align-items:center; }
  .payment-label { font-size:13px; color:#7a90b0; }
  .payment-value { font-size:13.5px; font-weight:600; color:#1a2b45; }
  .text-neg      { color:#e03535; }
  
  /* ── Payment Badges ── */
  .payment-badge      { display:inline-block; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; }
  .payment--cash      { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .payment--card      { background:#f5f3ff; color:#7c3aed; border-color:#c4b5fd; }
  .payment--transfer  { background:#edf4ff; color:#004AAD; border-color:#c5d6f5; }
  .payment--part,
  .payment--split     { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
  .payment--credit    { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  
  /* ── Print ── */
  @media print {
    .invoice { padding:20px; max-width:100%; }
  }
  
  /* ── Responsive ── */
  @media(max-width:768px) {
    .invoice          { padding:24px 20px; }
    .invoice-header   { flex-direction:column; }
    .sale-meta-grid   { grid-template-columns:repeat(2,1fr); }
    .invoice-parties  { flex-direction:column; }
    .party-block--right { text-align:left; }
    .totals-row, .totals-divider { width:100%; }
  }
  @media(max-width:480px) { .sale-meta-grid { grid-template-columns:1fr; } }
  </style>